import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  listPendingInvitations,
  sendInvitation,
  revokeInvitation,
  resendInvitation,
  type Invitation,
} from '@/services/invitations.service';

export type { Invitation };

export function useInvitations() {
  return useQuery({
    queryKey: ['invitations', 'pending'],
    queryFn: listPendingInvitations,
  });
}

export function useSendInvitation() {
  const qc = useQueryClient();
  const { i18n, t } = useTranslation();
  return useMutation({
    mutationFn: (input: {
      email: string;
      role: 'admin' | 'atendente' | 'prestador';
      locale?: string;
    }) =>
      sendInvitation({
        email: input.email,
        role: input.role,
        locale: input.locale ?? i18n.language ?? 'pt',
      }),
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      if ((data as { warning?: string })?.warning === 'email_failed') {
        toast.warning(t('invite.dialog.emailFailed', { email: vars.email }));
      } else {
        toast.success(t('invite.dialog.success', { email: vars.email }));
      }
    },
    onError: (e: Error) => {
      const fallback = t('invite.dialog.errors.GENERIC');
      const msg = t(`invite.dialog.errors.${e.message}`, { defaultValue: fallback });
      toast.error(msg);
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('invite.pending.revokeSuccess'));
    },
    onError: () => {
      toast.error(t('invite.pending.revokeError', { defaultValue: t('invite.dialog.errors.GENERIC') }));
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('invite.pending.resendSuccess'));
    },
    onError: () => {
      toast.error(t('invite.pending.resendError', { defaultValue: t('invite.dialog.errors.GENERIC') }));
    },
  });
}
